import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowUpOutlined,
  AuditOutlined,
  PrinterOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logout, setAccess } from "../../store/authSlice";
import { denyAccess } from "../../api/access";
import { useViewport } from "../../hooks/useViewport";
import BackButton from "../../components/BackButton";
import ProfileDropdown from "../../components/ProfileDropdown";

// Служебен модул „Мониторинг" — само за администратора (user id = 1).
// Трите карти водят към одита на действията, справката „Фискални проблеми"
// и AI помощника; самите страници също се гейтват по userId === 1 (а BE-то —
// с `require_admin`).
const Monitoring = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isNarrow, isDesktop } = useViewport();

  const userId = useAppSelector((s) => s.auth.userId);
  const status = useAppSelector((s) => s.auth.status);
  const access = useAppSelector((s) => s.auth.access);
  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const displayName = names || username || "";

  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch (error) {
        console.error("Deny access error:", error);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
    } else {
      await dispatch(logout());
      navigate("/login", { replace: true });
    }
  };

  if (status === "loading") return null;
  if (userId !== 1) return <Navigate to="/" replace />;

  const boxes = [
    {
      key: "audit-log",
      title: t("monitoring.auditLog"),
      desc: t("monitoring.auditLogDesc"),
      Icon: AuditOutlined,
      path: "/audit-log",
    },
    {
      key: "fiscal-issues",
      title: t("monitoring.fiscalIssues"),
      desc: t("monitoring.fiscalIssuesDesc"),
      Icon: PrinterOutlined,
      path: "/monitoring/fiscal-issues",
    },
    {
      key: "assistant",
      title: t("monitoring.assistant"),
      desc: t("monitoring.assistantDesc"),
      Icon: RobotOutlined,
      path: "/monitoring/assistant",
    },
  ];

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-canvas font-['Plus_Jakarta_Sans',-apple-system,BlinkMacSystemFont,system-ui,sans-serif] text-ink">
      {/* TopBar — същият шаблон като останалите страници (лого + заглавие) */}
      <div
        className={`flex items-center justify-between gap-[10px] border-b border-shade-2 ${
          isNarrow
            ? "min-h-[48px] px-[16px] py-[10px]"
            : "min-h-[60px] px-[28px] py-[11px]"
        }`}
        // Кехлибареният radial gradient сигнализира „ограничен достъп" —
        // зависи от access по време на изпълнение.
        style={{
          background: access
            ? "radial-gradient(ellipse 40% 120% at 100% 50%, var(--color-brand-tint) 0%, transparent 70%), var(--color-surface)"
            : "var(--color-surface)",
        }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-[10px]">
          <BackButton onClick={() => navigate("/")} />
          <img
            src="/logo-ts.png"
            alt="UnrealSoft"
            className={`shrink-0 select-none object-contain ${
              isNarrow ? "h-[26px] w-[26px]" : "h-[34px] w-[34px]"
            }`}
          />
          <div className="min-w-0 flex-1 leading-none">
            <h1
              className={`m-0 overflow-hidden font-bold leading-[1.2] tracking-[-0.3px] text-ellipsis whitespace-nowrap text-ink ${
                isNarrow ? "text-[16px]" : "text-[18px]"
              }`}
            >
              {t("monitoring.title")}
            </h1>
            {!isNarrow && (
              <div className="mt-[4px] text-[11px] leading-none tracking-[0.3px] text-muted">
                {t("monitoring.subtitle")}
              </div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-[10px]">
          {username && (
            <ProfileDropdown
              displayName={displayName}
              username={username}
              access={access}
              isNarrow={isNarrow}
              onLogout={handleLogout}
            />
          )}
        </div>
      </div>

      {/* Трите карти — центрирани, с щедро отстояние от всички страни */}
      <div
        className={`flex min-h-0 flex-1 items-center justify-center overflow-y-auto ${
          isNarrow ? "px-[20px] py-[24px]" : "px-[64px] py-[48px]"
        }`}
      >
        <div
          className={`grid w-full ${
            isNarrow
              ? "grid-cols-1 auto-rows-[220px] gap-[16px]"
              : `grid-cols-3 auto-rows-[min(460px,calc(100vh-220px))] ${
                  isDesktop ? "max-w-[1240px] gap-[56px]" : "gap-[20px]"
                }`
          }`}
        >
          {boxes.map(({ key, title, desc, Icon, path }) => (
            <button
              key={key}
              type="button"
              onClick={() => navigate(path)}
              // Визуалната рецепта е 1:1 с ModuleCard от „Модули": amber glow,
              // watermark икона, градиентен icon chip, amber border на hover
              className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-[16px] border border-shade-2 bg-surface transition-[border-color,box-shadow] duration-150 hover:border-[rgba(217,119,6,0.35)] hover:shadow-[0_12px_28px_rgba(17,17,17,0.07),0_3px_8px_rgba(17,17,17,0.04)]"
            >
              {/* Amber glow горе-дясно — засилва се на hover */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_65%_at_100%_0%,var(--color-brand-tint)_0%,transparent_60%)] opacity-60 transition-opacity duration-200 group-hover:opacity-100"
              />
              {/* Watermark икона долу-дясно */}
              <Icon
                aria-hidden
                className={`pointer-events-none absolute -rotate-[10deg] !text-[rgba(217,119,6,0.05)] transition-colors duration-200 group-hover:!text-[rgba(217,119,6,0.09)] ${
                  isNarrow
                    ? "-right-[12px] -bottom-[16px] !text-[110px]"
                    : "-right-[16px] -bottom-[24px] !text-[150px]"
                }`}
              />
              <span
                className={`absolute flex items-center justify-center rounded-[8px] text-muted transition-colors duration-150 group-hover:bg-brand-tint group-hover:text-brand-deep-ink ${
                  isNarrow
                    ? "top-[14px] right-[14px] h-[28px] w-[28px]"
                    : "top-[20px] right-[20px] h-[32px] w-[32px]"
                }`}
              >
                <ArrowUpOutlined
                  className={`rotate-45 ${isNarrow ? "!text-[16px]" : "!text-[19px]"}`}
                />
              </span>
              <div
                className={`relative flex h-full w-full flex-col items-center justify-center text-center ${
                  isNarrow ? "gap-[12px] p-[20px]" : "gap-[14px] p-[28px]"
                }`}
              >
                <span
                  className={`flex items-center justify-center rounded-[16px] border border-[rgba(217,119,6,0.16)] bg-[linear-gradient(135deg,var(--color-brand-tint)_0%,var(--color-brand-tint2)_100%)] ${
                    isNarrow ? "h-[56px] w-[56px]" : "h-[76px] w-[76px]"
                  }`}
                >
                  <Icon
                    className={`!text-brand-deep-ink ${
                      isNarrow ? "!text-[26px]" : "!text-[34px]"
                    }`}
                  />
                </span>
                <div
                  className={`font-bold leading-[1.2] tracking-[-0.3px] text-ink ${
                    isNarrow ? "text-[18px]" : "text-[22px]"
                  }`}
                >
                  {title}
                </div>
                <div
                  className={`max-w-[360px] font-medium leading-[1.55] text-body ${
                    isNarrow ? "text-[12.5px]" : "text-[13.5px]"
                  }`}
                >
                  {desc}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Monitoring;
